<template>
  <div class="page-content user-profile">
    
    <!-- USER PROFILE HEADLINE -->
    <div class="page-title">
      <span>{{ $lang.messages.familyProfile.toUpperCase() }}</span>
    </div>
    
    <!-- CO-PARENTS SECTION -->
    <div
        class="profile-content-row"
    >
      <div class="row profile-header profile-header-table">
        <span class="title">{{ $lang.messages.coparent }}</span>
        <span class="box-name-info" @click="coparentsTooltip = true" :class="{ 'blue-box-info': coparentsTooltip}"></span>
        <f-p-tooltip
            v-if="coparentsTooltip"
            v-click-outside="hideCoparentsTooltip"
            :hideTooltipInside="hideCoparentsTooltip"
            :tooltipTitle="$lang.messages.tp_userProfileCoparentsTitle"
            :tooltipContent="$lang.messages.tp_userProfileCoparentsText"
            :tooltipHasLink="''">
        </f-p-tooltip>
        
        <!-- ADD CO-PARENT -->
        <button
            class="generic-btn"
            type="button"
            @click="addCoparent"
            :class="{'arrow-btn' : showStep1Tooltip, 'enabled' : coparentAdded === 'current'}"
        >
          {{ $lang.messages.addCoparent }}
          <img class="btn-icon" src='@/assets/images/payments/plus_ico.png' />
        </button>
        
        <!-- MOBILE ADD CO-PARENT KEBAB ICON -->
        <span class="actions-ico" @click="toggleCoparentsActions()" :class="{'arrow-btn': showStep1Tooltip}">
          <span :class="{'blink' : showStep1Tooltip}"></span>
          <div>
            <img src="@/assets/images/payments/actions_ico.png" />
          </div>
        </span>
        
        <div class="action-menu" :class="[ coparentsActions ? 'select-menu-collapsed' : 'select-menu-not-collapsed', coparentAdded === 'current'? 'enabled' : '' ]" >
          <div>
            <button class="item btn-item" @click="addCoparent">
              {{ $lang.messages.addCoparent }}
            </button>
          </div>
        </div>
        
        <!-- Add New Coparent tooltip -->
        <f-p-registration-progress-tooltip
            v-if="showStep1Tooltip"
            :class="{'enabled' : coparentAdded === 'current'}"
            :stepNumber="'1'"
            style="top: calc((100% - 100px) / 2)"
            :text="$lang.messages.registration_progress_step_1">
        </f-p-registration-progress-tooltip>
        
        <!-- Info About added new coparent tooltip -->
        <template v-if="isShownAddedCoparent">
          <f-p-registration-progress-tooltip
              :stepNumber="'1'"
              :text="$lang.messages.registration_progress_coparent_added"
              :style="{top: `calc(100% + 25px)`}"
              :class="{
              'enabled': coparentAdded === 'finished',
              'arrow-tooltip--before': coparentAdded === 'finished',
              'got-it-tooltip': true
            }"
          >
            <div class="fp-registration-progress-tooltip-button">
              <button @click="isClickedGotItForCoparent">
                <span v-if="(coparentAdded === 'finished' && (childAdded === 'current' || childAdded === 'finished')) || (coparentAdded === 'finished' && totalSteps === 1)">
                  {{ $lang.messages.registration_progress_btn_got_it_1 }}
                </span>
                <span v-if="coparentAdded === 'finished' && !childAdded && totalSteps > 1">
                  {{ $lang.messages.registration_progress_btn_got_it_2 }}
                </span>
              </button>
            </div>
          </f-p-registration-progress-tooltip>
        </template>
        
        <!-- Add Record Keeping Co-Parent modal -->
        <add-coparent-modal
            @loadUserProfile="loadUserProfile(false, false)"
            :class="{'enabled' : coparentAdded === 'current'}"
            @handleAddCoparentModalOnClosing="handleAddCoparentModalOnClosing"
            ref="addCoparentComponent">
        </add-coparent-modal>
        
        <!-- Invite Co-Parent modal -->
        <invite-coparent-modal
            @loadUserProfile="loadUserProfile(false, false)"
            ref="inviteCoparentComponent">
        </invite-coparent-modal>
      </div>
      
      <div class="row" :class="{'app--locked': coparentAdded !== 'finished'}">
        <div class="col">
          <f-p-table-user-profile
              :list="user.userCoparents"
              :coparentsProp="true"
              :mockData="user.userCoparents.length === 0"
              @deleteCoparent="deleteCoparent"
              @editCoparent="editCoparent"
              @resendInvitation="sendOrResendInvitation"
              @cancelInvitation="cancelInvitation">
          </f-p-table-user-profile>
        </div>
      </div>
    </div>
    
    <!-- CHILDREN SECTION-->
    <div
        class="profile-content-row"
        :class="{'app--locked': childAdded !== 'finished' && showStep1Tooltip}"
    >
      <div class="row profile-header profile-header-table">
        <span class="title">{{ $lang.messages.children }}</span>
        <span class="box-name-info" @click="childrenTooltip = true" :class="{ 'blue-box-info': childrenTooltip}"></span>
        <f-p-tooltip
            v-if="childrenTooltip"
            v-click-outside="hideChildrenTooltip"
            :hideTooltipInside="hideChildrenTooltip"
            :tooltipTitle="$lang.messages.tp_userProfileChildrenTitle"
            :tooltipContent="$lang.messages.tp_userProfileChildrenText"
            :tooltipHasLink="''">
        </f-p-tooltip>
        
        <button
            class="generic-btn btn-child"
            type="button"
            @click="addChild"
            :class="{'arrow-btn add-child-btn' : showStep2Tooltip, 'enabled': childAdded === 'current'}"
            :disabled="!(ifParents && coparentAdded === 'finished')"
        >
          {{ $lang.messages.addChild }}
          <img class="btn-icon" src='@/assets/images/payments/plus_ico.png' />
        </button>
        
        <span class="actions-ico" @click="toggleChildrenActions()" :class="{'arrow-btn': showStep2Tooltip}">
          <span :class="{'blink' : showStep2Tooltip}"></span>
          <div>
            <img src="@/assets/images/payments/actions_ico.png" />
          </div>
        </span>
        
        <div class="action-menu" :class="[ childrenActions ? 'select-menu-collapsed' : 'select-menu-not-collapsed', childAdded === 'current' ? 'enabled' : '' ]">
          <div>
            <button class="item btn-item" @click="addChild">
              {{ $lang.messages.addChild }}
            </button>
          </div>
        </div>
        
        <template v-if="isShownAddedChild">
          <f-p-registration-progress-tooltip
              :stepNumber="'2'"
              :style="{top: `calc(100% + 25px)`}"
              :text="$lang.messages.registration_progress_child_added"
              :class="{
              'enabled': childAdded === 'finished',
              'arrow-tooltip--before': childAdded === 'finished',
              'got-it-tooltip': true
            }"
          >
            <div class="fp-registration-progress-tooltip-button">
              <button @click="isClickedGotItForChild">
                {{ $lang.messages.registration_progress_btn_got_it_2 }}
              </button>
            </div>
          </f-p-registration-progress-tooltip>
        </template>
        
        <f-p-registration-progress-tooltip
            v-if="showStep2Tooltip"
            :stepNumber="'2'"
            style="top: calc((100% - 65px) / 2)"
            :class="{'enabled': childAdded === 'current', 'add-child-tooltip' : true}"
            :text="$lang.messages.registration_progress_step_2">
        </f-p-registration-progress-tooltip>
        
        <!-- Add Child modal -->
        <add-child-modal
            @loadUserProfile="loadUserProfile(false, false)"
            @handleAddChildModalOnClosing="handleAddChildModalOnClosing"
            :class="{'enabled': childAdded === 'current'}"
            ref="addChildComponent">
        </add-child-modal>
      </div>
      
      <div class="row">
        <div class="col">
          <f-p-table-user-profile
              :list="user.userChildren"
              :coparentsList="user.userCoparents"
              :childrenProp="true"
              :mockData="user.userChildren.length === 0"
              @deleteChild="deleteChild"
              @editChild="editChild">
          </f-p-table-user-profile>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { mapActions, mapMutations, mapGetters } from "vuex";
  import {
    GET_FAMILY_PROFILE,
    DELETE_COPARENT,
    DELETE_CHILD,
    INVITE_COPARENT,
    RESEND_INVITITATION_COPARENT,
    CANCEL_INVITATION_COPARENT
  } from "@/store/action.types";
  import {
    SET_HAS_COPARENT,
    SET_HAS_CHILDREN,
    SET_HAS_RECORD_KEEPING_COPARENTS
  } from "@/store/mutation.types";
  import FPTableUserProfile from "@/components/common/tables/FPTableUserProfile";
  import Autocomplete from "@/components/common/Autocomplete";
  import Datepicker from 'vuejs-datepicker';
  import AddCoparentModal from "@/components/appLayout/views/profile/AddCoparentModal";
  import InviteCoparentModal from "@/components/appLayout/views/profile/InviteCoparentModal";
  import AddChildModal from "@/components/appLayout/views/profile/AddChildModal";
  import FPRegProgressTooltip from '@/components/common/FPRegistrationProgressTooltip';
  import { dateFilter } from 'vue-date-fns';
  import vClickOutside from 'v-click-outside';
  import { dateFormat } from '@/mixins/dateFormat';
  import STATUS_CODES from "@/common/statusCodes";
  
  export default {
    name: "FamilyProfile",
    components: {
      'f-p-tooltip': () => import('@/components/common/FPTooltip'),
      'f-p-registration-progress-tooltip': FPRegProgressTooltip,
      FPTableUserProfile,
      Autocomplete,
      Datepicker,
      AddCoparentModal,
      InviteCoparentModal,
      AddChildModal
    },
    mixins: [
      dateFormat
    ],
    directives: {
      clickOutside: vClickOutside.directive
    },
    filters: {
      date: dateFilter
    },
    data() {
      return {
        user: {
          userChildren: [],
          userCoparents: []
        },
        coparentActionType: "",
        coparentToAdd: {
          firstName: "",
          lastName: "",
          birthDate: new Date(),
          invited: false
        },
        childActionType: "",
        childToAdd: {
          firstName: "",
          lastName: "",
          coparent: {},
          birthDate: new Date()
        },
        coparentsActions: false,
        childrenActions: false,
        ifParents: false,
        coparentsTooltip: false,
        childrenTooltip: false,
        addCoparentModalOpened: false,
        addChildModalOpened: false,
        isShownAddedCoparentProp: false,
        isShownAddedChildProp: false,
      };
    },
    computed: {
      ...mapGetters([
        "coparentAdded",
        "childAdded",
        "totalSteps"
      ]),
      showStep1Tooltip() {
        let isCoparentAdded = this.coparentAdded === 'current';
        return isCoparentAdded && !this.addCoparentModalOpened;
      },
      showStep2Tooltip() {
        let isChildAdded = this.childAdded === 'current';
        return isChildAdded && !this.addChildModalOpened;
      },
      isShownAddedCoparent() {
        const result = this.coparentAdded === 'finished' && this.isShownAddedCoparentProp;
        return result;
      },
      isShownAddedChild() {
        const result = this.childAdded === 'finished' && this.isShownAddedChildProp;
        return result
      }
    },
    mounted() {
      this.loadUserProfile(false, false);
      this.isShownAddedCoparentProp = JSON.parse(window.localStorage.getItem('isShownTooltipForAddedCoparent'));
      this.isShownAddedChildProp = JSON.parse(window.localStorage.getItem('isShownTooltipForAddedChild'));
    },
    methods: {
      ...mapActions({
        getFamilyProfileAction: GET_FAMILY_PROFILE,
        deleteCoparentAction: DELETE_COPARENT,
        deleteChildAction: DELETE_CHILD,
        inviteCoparentAction: INVITE_COPARENT,
        resendInvitationCoparentAction: RESEND_INVITITATION_COPARENT,
        cancelInvitationAction: CANCEL_INVITATION_COPARENT
      }),
      ...mapMutations({
        hasCoparentMutation: SET_HAS_COPARENT,
        hasChildrenMutation: SET_HAS_CHILDREN,
        hasRecordKeepingCoparentsMutation: SET_HAS_RECORD_KEEPING_COPARENTS
      }),
      loadUserProfile(showGotItCoparent, showGotItChild) {
        this.getFamilyProfileAction()
        .then(response => {
          this.user = response.data.data;
          
          this.user.userChildren.forEach(item => {
            item.guid = item.childGuid;
          });
          
          if (this.user.userCoparents.length > 0) {
            this.ifParents = true;
            this.$parent.disconnectWebsocket();
            this.$parent.loadContacts();
          }
          
          this.setLocalStorageForAddedCoparentRegistration(showGotItCoparent);
          this.setLocalStorageForAddedChildRegistration(showGotItChild);
          
          this.checkHasCoparentProp();
          this.checkHasChildrenProp();
        })
        .catch(error => {
          if (error.response) {
            if (error.response.status !== 406 && error.response.data.status.code !== STATUS_CODES.FP_405 && error.response.status !== 401 && error.response.status !== 402) {
              this.$snotify.error(this.$lang.messages.sn_genericError, 'ERROR')
            }
          }
        });
      },
      showAddCoparentModal() {
        this.addCoparentModalOpened = true;
        this.$refs.addCoparentComponent.show(this.coparentActionType, this.coparentToAdd)
      },
      /**
       * @description When we close the modal, and we didn't call the modal action
       * close the modal and eventually show the tooltips
       * @param {String }val
       */
      handleAddCoparentModalOnClosing(val) {
        this.addCoparentModalOpened = val;
      },
      showInviteCoparentModal(item) {
        this.$refs.inviteCoparentComponent.show(item)
      },
      showAddChildModal() {
        this.addChildModalOpened = true;
        this.$refs.addChildComponent.show(this.childActionType, this.childToAdd)
      },
      /**
       * @description When we close the modal, and we didn't call the modal action
       * close the modal and eventually show the tooltips
       * @param {String }val
       */
      handleAddChildModalOnClosing (val) {
        this.addChildModalOpened = val;
      },
      sendOrResendInvitation(item) {
        if (item.invitationStatus === 'NOT_INVITED') {
          this.showInviteCoparentModal(item)
        } else {
          this.resendInvitation(item)
        }
      },
      addCoparent() {
        this.coparentsActions = false;
        this.coparentActionType = 'add';
        this.showAddCoparentModal();
      },
      editCoparent(item) {
        this.coparentActionType = 'edit';
        this.coparentToAdd.firstName = item.firstName;
        this.coparentToAdd.lastName = item.lastName;
        this.coparentToAdd.email = item.email;
        this.coparentToAdd.guid = item.guid;
        this.coparentToAdd.coparentType = item.coparentType;
        this.coparentToAdd.invited = item.email !== '';
        this.showAddCoparentModal()
      },
      deleteCoparent(guid) {
        this.deleteCoparentAction(guid)
        .then(() => {
          this.loadUserProfile(false, false);
          this.$parent.loadCoparents();
          this.$snotify.success(this.$lang.messages.sn_coparentDeleted, 'SUCCESS');
          if (this.user.userCoparents.length === 1) {
            this.ifParents = false;
          }
        })
        .catch(error => {
          if (error.response) {
            if (error.response.status !== 406 && error.response.data.status.code !== STATUS_CODES.FP_405 && error.response.status !== 401 && error.response.status !== 402) {
              this.$snotify.error(error.status.desc, 'ERROR')
            }
          }
        });
      },
      resendInvitation(item) {
        this.resendInvitationCoparentAction({
          email: item.email,
          invitedCoparentGuid: item.guid,
        })
        .then(() => {
          this.loadUserProfile(false, false);
          this.$snotify.success(this.$lang.messages.sn_successCoparentInvited, "SUCCESS");
        })
        .catch(error => {
          if (error.response.status !== 406 && error.response.data.status.code !== STATUS_CODES.FP_405 && error.response.status !== 401 && error.response.status !== 402) {
            this.$snotify.error(error.response.data.status.desc, "ERROR")
          }
        });
      },
      cancelInvitation(item) {
        this.cancelInvitationAction(item.guid)
        .then(() => {
          this.loadUserProfile(false, false);
          this.$snotify.success(this.$lang.messages.sn_coparentDeleted, 'SUCCESS');
        })
        .catch(error => {
          if (error.response) {
            if (error.response.status !== 406 && error.response.data.status.code !== STATUS_CODES.FP_405 && error.response.status !== 401 && error.response.status !== 402) {
              this.$snotify.error(error.status.desc, 'ERROR')
            }
          }
        });
      },
      addChild() {
        this.childrenActions = false
        this.childActionType = 'add'
        this.showAddChildModal()
      },
      editChild(item) {
        this.childActionType = 'edit';
        this.childToAdd.firstName = item.firstName;
        this.childToAdd.lastName = item.lastName;
        this.childToAdd.birthDate = item.birthDate;
        this.childToAdd.guid = item.childGuid;
        this.showAddChildModal()
      },
      deleteChild(guid) {
        this.deleteChildAction(guid)
        .then(() => {
          this.loadUserProfile(false, false)
          this.$parent.loadCoparents();
          this.$snotify.success(this.$lang.messages.sn_childDelete, 'SUCCESS')
        })
        .catch(error => {
          if (error.response) {
            if (error.response.status !== 406 && error.response.data.status.code !== STATUS_CODES.FP_405 && error.response.status !== 401 && error.response.status !== 402) {
              this.$snotify.error(error.response.data.status.desc, 'ERROR')
            }
          }
        });
      },
      toggleCoparentsActions() {
        this.coparentsActions = !this.coparentsActions
      },
      toggleChildrenActions() {
        this.childrenActions = !this.childrenActions
      },
      checkHasCoparentProp() {
        if (!this.user.userCoparents.length) {
          this.hasCoparentMutation(false)
          this.hasRecordKeepingCoparentsMutation(false)
        } else if (this.user.userCoparents.length) {
          let anyRegistered = false
          let anyRecordKeeping = false
          this.user.userCoparents.forEach(coparent => {
            if (coparent.invitationStatus === 'INVITATION_ACCEPTED') {
              anyRegistered = true
            }
            if (coparent.invitationStatus === 'INVITATION_SENT' || coparent.invitationStatus === 'INVITATION_EXPIRED' || coparent.invitationStatus === 'NOT_INVITED') {
              anyRecordKeeping = true
            }
          });
          this.hasCoparentMutation(anyRegistered)
          this.hasRecordKeepingCoparentsMutation(anyRecordKeeping)
        }
      },
      checkHasChildrenProp() {
        if (this.user.userChildren.length === 0) {
          this.hasChildrenMutation(false)
        } else if (this.user.userChildren.length >= 1) {
          this.hasChildrenMutation(true)
        }
      },
      hideCoparentsTooltip(e) {
        e.stopPropagation();
        this.coparentsTooltip = false;
      },
      hideChildrenTooltip(e) {
        e.stopPropagation();
        this.childrenTooltip = false;
      },
      /**
       * @description On component mounted set the initial values for registration steps for
       * added coparent
       */
      setLocalStorageForAddedCoparentRegistration(showGotItCoparent) {
        const condition = showGotItCoparent && this.user.userCoparents.length === 1 && (this.coparentAdded === 'finished' || this.coparentToAdd === 'current');
        if (condition) {
          window.localStorage.setItem('isShownTooltipForAddedCoparent', JSON.stringify(true));
          this.isShownAddedCoparentProp = JSON.parse(window.localStorage.getItem('isShownTooltipForAddedCoparent'));
        }
      },
      /**
       * @description On component mounted set the initial values for registration steps for
       * added child
       */
      setLocalStorageForAddedChildRegistration(showGotItChild) {
        const condition = showGotItChild && this.user.userChildren.length === 1 && (this.childAdded === 'finished' || this.childAdded === 'current');
        if (condition) {
          window.localStorage.setItem('isShownTooltipForAddedChild', JSON.stringify(true));
          this.isShownAddedChildProp = JSON.parse(window.localStorage.getItem('isShownTooltipForAddedChild'));
        }
      },
      /**
       * @description When user clicks on tooltip for added coparent button, hide the tooltip
       */
      isClickedGotItForCoparent() {
        window.localStorage.setItem('isShownTooltipForAddedCoparent', JSON.stringify(false));
        this.isShownAddedCoparentProp = JSON.parse(window.localStorage.getItem('isShownTooltipForAddedCoparent'));
        
        if (this.coparentAdded === 'finished' && !this.childAdded && this.totalSteps > 1) {
          this.$router.push({ name: 'PaymentMethods' })
        } else if (this.coparentAdded === 'finished' && this.totalSteps === 1) {
          this.$router.push({ name: 'Dashboard' });
        }
      },
      /**
       * @description When user clicks on tooltip for added child button, hide the tooltip
       */
      isClickedGotItForChild() {
        window.localStorage.setItem('isShownTooltipForAddedChild', JSON.stringify(false));
        this.isShownAddedChildProp = JSON.parse(window.localStorage.getItem('isShownTooltipForAddedChild'));
        this.$router.push('PaymentMethods');
      },
    }
  };
</script>